import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PawPrint, Eye, EyeOff, KeyRound } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import appApi, { saveSession, setPin as savePin, bioAvailable, bioEnroll } from "./appApi";
import { AuthBackdrop, PinPad, ScreenIn, BusyOverlay } from "./bits";
import { LOGO_URL } from "@/i18n/translations";

// Code-only login (no username) — the code identifies the staff member. Free text (letters allowed).
export const CodeLogin = ({ onLoggedIn, onDeactivated }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [code, setCode] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = async () => {
    if (code.trim().length < 4 || busy) return;
    setBusy(true);
    try {
      const res = await appApi.post("/app/auth/login", { code: code.trim() });
      saveSession(res.data.access_token, res.data.user);
      onLoggedIn(res.data.user);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) { onDeactivated?.(); return; }
      const detail = err.response?.data?.detail;
      if (status === 429) toast.error(typeof detail === "string" ? detail : m.error_generic);
      else toast.error(m.code_wrong);
      setShake(true);
      setCode("");
      setTimeout(() => setShake(false), 450);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthBackdrop>
      <ScreenIn k="login">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-14 min-h-screen">
          <motion.img
            src={LOGO_URL}
            alt="Care For Me"
            className="h-14 object-contain"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          />
          <motion.h1
            className="mt-8 font-heading text-2xl font-medium text-brand-ink text-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            data-testid="code-login-title"
          >
            {m.code_title}
          </motion.h1>
          <p className="mt-1.5 text-sm text-brand-muted text-center">{m.code_sub}</p>
          <motion.div
            animate={shake ? { x: [0, -10, 10, -8, 8, 0] } : {}}
            transition={{ duration: 0.4 }}
            className="mt-8 w-full max-w-xs relative"
          >
            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand/40" />
            <input
              type={show ? "text" : "password"}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              autoComplete="off"
              autoFocus
              className="w-full h-14 rounded-2xl border border-brand/20 bg-white pl-12 pr-12 text-lg text-brand-ink tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-brand/40 shadow-[0_6px_20px_rgba(134,77,99,0.08)]"
              data-testid="code-input"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-ink/40"
              data-testid="code-show-toggle"
              aria-label="show"
            >
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </motion.div>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={submit}
            disabled={code.trim().length < 4 || busy}
            className="mt-6 w-full max-w-xs h-14 rounded-2xl bg-brand text-brand-cream font-bold text-lg shadow-[0_12px_30px_rgba(134,77,99,0.35)] disabled:opacity-40"
            data-testid="code-login-submit"
          >
            {m.login_btn}
          </motion.button>
        </div>
      </ScreenIn>
      <BusyOverlay show={busy} label={m.loading} />
    </AuthBackdrop>
  );
};

// Deactivated account screen — shown on login/boot when the owner has deactivated the user
export const DeactivatedScreen = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  return (
    <AuthBackdrop>
      <div className="flex-1 flex flex-col items-center justify-center px-8 min-h-screen text-center" data-testid="app-deactivated">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 16 }}
          className="grid place-items-center h-20 w-20 rounded-full bg-rose-100 text-rose-500"
        >
          <PawPrint className="h-9 w-9" />
        </motion.div>
        <h2 className="mt-6 font-heading text-2xl font-medium text-brand-ink">{m.deactivated_title}</h2>
        <p className="mt-2 text-sm text-brand-ink/70 max-w-xs">{m.deactivated_msg}</p>
        <p className="mt-4 text-sm text-brand-ink/70">
          {m.deactivated_contact}{" "}
          <a href="tel:6972367229" className="font-bold text-brand no-a11y-underline" data-testid="deactivated-call">Ανδρέας — 6972367229</a>
        </p>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onBack}
          className="mt-8 rounded-full bg-brand text-brand-cream font-bold px-8 h-12"
          data-testid="deactivated-back"
        >
          {m.deactivated_back}
        </motion.button>
      </div>
    </AuthBackdrop>
  );
};

// PIN setup (optional) after first code login
export const PinSetup = ({ user, onDone }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [step, setStep] = useState(0); // 0 choose, 1 confirm
  const [first, setFirst] = useState("");
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [bio, setBio] = useState(null); // null unknown, true offer, false skip

  const change = async (v) => {
    setPin(v);
    if (v.length < 4) return;
    if (step === 0) {
      setFirst(v);
      setPin("");
      setStep(1);
    } else {
      if (v === first) {
        await savePin(user.id, v);
        if (await bioAvailable()) setBio(true);
        else onDone();
      } else {
        setShake(true);
        toast.error(m.pin_mismatch);
        setPin("");
        setStep(0);
        setFirst("");
        setTimeout(() => setShake(false), 450);
      }
    }
  };

  if (bio) {
    return (
      <AuthBackdrop>
        <div className="flex-1 flex flex-col items-center justify-center px-8 min-h-screen text-center">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 16 }}
            className="grid place-items-center h-20 w-20 rounded-full bg-brand text-brand-cream shadow-lg"
          >
            <PawPrint className="h-9 w-9" />
          </motion.div>
          <h2 className="mt-6 font-heading text-2xl font-medium text-brand-ink">{m.bio_offer_title}</h2>
          <p className="mt-2 text-sm text-brand-muted max-w-xs">{m.bio_offer_sub}</p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={async () => { try { await bioEnroll(user); } catch { /* unavailable */ } onDone(); }}
            className="mt-8 w-full max-w-xs h-13 py-3.5 rounded-2xl bg-brand text-brand-cream font-bold shadow-lg"
            data-testid="bio-enable"
          >
            {m.bio_enable}
          </motion.button>
          <button onClick={onDone} className="mt-4 text-sm font-bold text-brand-ink/50" data-testid="bio-later">{m.bio_later}</button>
        </div>
      </AuthBackdrop>
    );
  }

  return (
    <AuthBackdrop>
      <ScreenIn k={`pinsetup-${step}`}>
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-14 min-h-screen">
          <h2 className="font-heading text-2xl font-medium text-brand-ink text-center" data-testid="pin-setup-title">
            {step === 0 ? m.pin_choose : m.pin_confirm}
          </h2>
          <p className="mt-1.5 text-sm text-brand-muted text-center max-w-xs">{m.pin_setup_sub}</p>
          <div className="mt-8 w-full">
            <PinPad value={pin} onChange={change} maxLen={4} shake={shake} />
          </div>
          <button onClick={onDone} className="mt-8 text-sm font-bold text-brand-ink/50" data-testid="pin-skip">{m.pin_skip}</button>
        </div>
      </ScreenIn>
    </AuthBackdrop>
  );
};
